import { useState, useEffect } from 'react';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import type {
  Empresa,
  Operadora,
  Equipo,
  BombaPiston,
  EquipoBomba,
  PiletaTipo,
  PiletaEquipo,
  Pad,
  Pozo,
  Tramo,
  Trepano,
  Material,
  ServicioTipo,
  LookupTable,
  EquipmentBrand,
  SolidsDischargeType,
  ScreenType,
  DailyActivity,
  MudType,
  PhaseOleosa,
  Densificante,
  ApiMesh,
  MeshMovement,
  BowlSize,
  ConeCountOption,
  DensidadLGS,
  ContainerMeasures
} from '../types';

const compareText = (a?: string, b?: string) =>
  (a || '').localeCompare(b || '', 'es', { numeric: true, sensitivity: 'base' });

const compareNumber = (a?: number, b?: number) =>
  (Number(a) || 0) - (Number(b) || 0);

export const useDashboardData = (equipoSeleccionado: string, pozoPadSelec: string, filterPad: string, tramoPozoSelec: string) => {
  // Core Entity States
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [operadoras, setOperadoras] = useState<Operadora[]>([]);
  const [equipos, setEquipos] = useState<Equipo[]>([]);
  const [bombasPiston, setBombasPiston] = useState<BombaPiston[]>([]);
  const [equipoBombas, setEquipoBombas] = useState<EquipoBomba[]>([]);
  const [tiposPiletas, setTiposPiletas] = useState<PiletaTipo[]>([]);
  const [piletasEquipo, setPiletasEquipo] = useState<PiletaEquipo[]>([]);
  const [pads, setPads] = useState<Pad[]>([]);
  const [pozos, setPozos] = useState<Pozo[]>([]);
  const [tramos, setTramos] = useState<Tramo[]>([]);
  const [trepanos, setTrepanos] = useState<Trepano[]>([]);

  // Excel Reporting Data
  const [materiales, setMateriales] = useState<Material[]>([]);
  const [serviciosTipo, setServiciosTipo] = useState<ServicioTipo[]>([]);
  const [lookupTables, setLookupTables] = useState<LookupTable[]>([]);

  // Excel Hardcoded Lists
  const [equipmentBrands, setEquipmentBrands] = useState<EquipmentBrand[]>([]);
  const [solidsDischargeTypes, setSolidsDischargeTypes] = useState<SolidsDischargeType[]>([]);
  const [screenTypes, setScreenTypes] = useState<ScreenType[]>([]);
  const [bowlSizes, setBowlSizes] = useState<BowlSize[]>([]);
  const [coneCountOptions, setConeCountOptions] = useState<ConeCountOption[]>([]);

  // Report Management Data
  const [dailyActivities, setDailyActivities] = useState<DailyActivity[]>([]);
  const [mudTypes, setMudTypes] = useState<MudType[]>([]);
  const [phaseOleosa, setPhaseOleosa] = useState<PhaseOleosa[]>([]);
  const [densificantes, setDensificantes] = useState<Densificante[]>([]);

  // LGS Calculations & Container Measures
  const [densidadesLGS, setDensidadesLGS] = useState<DensidadLGS[]>([]);
  const [containerMeasures, setContainerMeasures] = useState<ContainerMeasures | null>(null);

  // API Mesh/Screens Management Data
  const [apiMeshes, setApiMeshes] = useState<ApiMesh[]>([]);
  const [meshMovements, setMeshMovements] = useState<MeshMovement[]>([]);

  // Empresas Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'empresas'), (snapshot) => {
      const list = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Empresa))
        .sort((a, b) => compareText(a.nombre, b.nombre));
      setEmpresas(list);
    });
    return unsubscribe;
  }, []);

  // Operadoras Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'operadoras'), (snapshot) => {
      const list = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Operadora))
        .sort((a, b) => compareText(a.nombre, b.nombre));
      setOperadoras(list);
    });
    return unsubscribe;
  }, []);

  // Equipos Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'equipos'), (snapshot) => {
      const list = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Equipo))
        .sort((a, b) => compareText(a.operadoraId, b.operadoraId) || compareText(a.nombre, b.nombre));
      setEquipos(list);
    });
    return unsubscribe;
  }, []);

  // Piston Pumps Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'bombasPiston'), (snapshot) => {
      const list = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as BombaPiston))
        .sort((a, b) =>
          compareText(a.nombre, b.nombre) ||
          compareText(a.modelo, b.modelo) ||
          compareNumber(a.diametroIn, b.diametroIn)
        );
      setBombasPiston(list);
    });
    return unsubscribe;
  }, []);

  // Pump Assignments Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'equipoBombas'), (snapshot) => {
      const list = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as EquipoBomba))
        .sort((a, b) =>
          compareText(a.equipoId, b.equipoId) ||
          compareNumber(a.slot, b.slot) ||
          compareText(a.bombaPistonId, b.bombaPistonId)
        );
      setEquipoBombas(list);
    });
    return unsubscribe;
  }, []);

  // Piletas Tipos - live lookup so CSV/name edits are reflected immediately.
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'piletasTipos'), (snapshot) => {
      setTiposPiletas(snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as PiletaTipo))
        .sort((a, b) => compareText(a.nombre, b.nombre))
      );
    });
    return unsubscribe;
  }, []);

  // Piletas Equipo Listener (admin mode: load ALL piletas, or filtered by selected equipment)
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'piletas'), (snapshot) => {
      const list = snapshot.docs
        .filter(doc => !equipoSeleccionado || doc.data().equipoId === equipoSeleccionado) // Load all if no equipment selected
        .map(doc => ({ ...doc.data(), id: doc.id } as PiletaEquipo))
        .sort((a, b) => compareText(a.equipoId, b.equipoId) || compareText(a.nombre, b.nombre));
      setPiletasEquipo(list);
      console.log(`🔍 Loaded ${list.length} piletas${equipoSeleccionado ? ` for equipment: ${equipoSeleccionado}` : ' (all piletas - admin mode)'}`);
    });
    return unsubscribe;
  }, [equipoSeleccionado]);

  // Pads Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'pads'), (snapshot) => {
      const list = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Pad))
        .sort((a, b) =>
          compareText(a.operadoraId, b.operadoraId) ||
          compareText(a.equipoId, b.equipoId) ||
          compareText(a.nombre, b.nombre)
        );
      setPads(list);
    });
    return unsubscribe;
  }, []);

  // Pozos Listener (filtered by pad selection, or ALL pozos if no pad selected - admin mode)
  useEffect(() => {
    const currentPadId = pozoPadSelec || filterPad;

    const unsubscribe = onSnapshot(collection(db, 'pozos'), (snapshot) => {
      const list = snapshot.docs
        .filter(doc => !currentPadId || doc.data().padId === currentPadId) // Load all if no pad selected
        .map(doc => ({ id: doc.id, ...doc.data() } as Pozo))
        .sort((a, b) => compareText(a.padId, b.padId) || compareText(a.nombre, b.nombre));
      setPozos(list);
      console.log(`🔍 Loaded ${list.length} pozos${currentPadId ? ` for pad: ${currentPadId}` : ' (all pozos - admin mode)'}`);
    });
    return unsubscribe;
  }, [pozoPadSelec, filterPad]);

  // Tramos Listener (filtered by selected pozo, or ALL tramos if no pozo selected - admin mode)
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'tramos'), (snapshot) => {
      const list = snapshot.docs
        .filter(doc => !tramoPozoSelec || doc.data().pozoId === tramoPozoSelec) // Load all if no pozo selected
        .map(doc => ({ id: doc.id, ...doc.data() } as Tramo))
        .sort((a, b) =>
          compareText(a.pozoId, b.pozoId) ||
          compareText(a.fase, b.fase) ||
          compareNumber(a.profundidadObjetivo, b.profundidadObjetivo)
        );
      setTramos(list);
      console.log(`🔍 Loaded ${list.length} tramos${tramoPozoSelec ? ` for pozo: ${tramoPozoSelec}` : ' (all tramos - admin mode)'}`);
    });
    return unsubscribe;
  }, [tramoPozoSelec]);

  // Static lookup data - fetch once on mount (no real-time needed)
  useEffect(() => {
    const fetchLookupData = async () => {
      const fetchCollection = async <T,>(name: string, setter: (list: T[]) => void, transform?: (list: T[]) => T[]) => {
        try {
          const snapshot = await getDocs(collection(db, name));
          let list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as T));
          if (transform) list = transform(list);
          setter(list);
        } catch (e) {
          console.warn(`Failed to fetch ${name}:`, e);
        }
      };

      await Promise.all([
        fetchCollection<Trepano>('trepanos', setTrepanos, (list) => list.sort((a, b) => compareText(a.tamanio, b.tamanio) || compareText(a.nombre, b.nombre))),
        fetchCollection<Material>('materiales', setMateriales, (list) => list.sort((a, b) => compareText(a.categoria, b.categoria) || compareText(a.nombreComercial, b.nombreComercial))),
        fetchCollection<ServicioTipo>('serviciosTipo', setServiciosTipo, (list) => list.sort((a, b) => compareText(a.item, b.item) || compareText(a.subItem, b.subItem) || compareText(a.descripcion, b.descripcion))),
        fetchCollection<LookupTable>('lookupTables', setLookupTables, (list) => list.sort((a, b) => compareText(a.tipo, b.tipo) || compareText(a.nombre, b.nombre))),
        fetchCollection<EquipmentBrand>('equipmentBrands', setEquipmentBrands, (list) => list.sort((a, b) => compareText(a.equipmentType, b.equipmentType) || compareText(a.brand, b.brand) || compareText(a.model, b.model))),
        fetchCollection<SolidsDischargeType>('solidsDischargeTypes', setSolidsDischargeTypes, (list) => list.sort((a, b) => compareText(a.type, b.type))),
        fetchCollection<ScreenType>('screenTypes', setScreenTypes, (list) => list.sort((a, b) => compareNumber(a.mesh, b.mesh))),
        fetchCollection<DailyActivity>('dailyActivities', setDailyActivities, (list) => list.sort((a, b) => compareNumber(a.order, b.order) || compareText(a.displayName, b.displayName))),
        fetchCollection<MudType>('mudTypes', setMudTypes, (list) => list.sort((a, b) => compareText(a.type, b.type) || compareText(a.displayName, b.displayName))),
        fetchCollection<PhaseOleosa>('phaseOleosa', setPhaseOleosa, (list) => list.sort((a, b) => compareText(a.name, b.name))),
        fetchCollection<Densificante>('densificantes', setDensificantes, (list) => list.sort((a, b) => compareText(a.material, b.material))),
        fetchCollection<DensidadLGS>('densidadesLGS', setDensidadesLGS, (list) => list.sort((a, b) => b.value - a.value)),
        fetchCollection<ApiMesh>('apiMeshes', setApiMeshes, (list) => list.sort((a, b) => compareText(a.equipmentType, b.equipmentType) || compareNumber(a.mesh, b.mesh))),
        fetchCollection<MeshMovement>('meshMovements', setMeshMovements, (list) => list.sort((a, b) => compareText(String(b.fecha), String(a.fecha)))),
        fetchCollection<BowlSize>('bowlSizes', setBowlSizes, (list) => list.sort((a, b) => compareText(a.size, b.size))),
        fetchCollection<ConeCountOption>('coneCountOptions', setConeCountOptions, (list) => list.sort((a, b) => compareText(a.equipmentType, b.equipmentType) || compareNumber(a.count, b.count))),
      ]);

      // Container Measures - single document
      try {
        const cmSnapshot = await getDocs(collection(db, 'containerMeasures'));
        if (!cmSnapshot.empty) {
          const doc = cmSnapshot.docs[0];
          setContainerMeasures({ id: doc.id, ...doc.data() } as ContainerMeasures);
        }
      } catch (e) {
        console.warn('Failed to fetch containerMeasures:', e);
      }
    };

    fetchLookupData();
  }, []);

  return {
    // Core Entity Data
    operadoras,
    equipos,
    bombasPiston,
    equipoBombas,
    tiposPiletas,
    piletasEquipo,
    pads,
    pozos,
    tramos,
    trepanos,

    // Excel Reporting Data
    materiales,
    serviciosTipo,
    lookupTables,

    // Excel Hardcoded Lists
    empresas,
    equipmentBrands,
    solidsDischargeTypes,
    screenTypes,
    bowlSizes,
    coneCountOptions,

    // Report Management Data
    dailyActivities,
    mudTypes,
    phaseOleosa,
    densificantes,

    // LGS Calculations & Container Measures
    densidadesLGS,
    containerMeasures,

    // API Mesh/Screens Management Data
    apiMeshes,
    meshMovements
  };
};
